import { ChangeDetectorRef, Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SetupScreensService } from '../../services/setup-screens.service';
import { Store } from '@ngxs/store';
import { Alert, AlertService, AlertType, ModalComponent } from '@michelin/theme';
import { EGRA_POSTS } from '../../models/zone-posts.model';
import { WorkFlowDetails, ZoneDetailsCompositeResponse, ZoneProcessCode, ZoneSetup } from '../../models/zone-setup.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-zone-setup-details',
  templateUrl: './zone-setup-details.component.html',
  styleUrls: ['./zone-setup-details.component.scss'],
  standalone: false
})
export class ZoneSetupDetailsComponent implements OnInit, CanComponentDeactivate {
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public translationService = inject(TranslationService);
  public fb = inject(FormBuilder);
  public cdr = inject(ChangeDetectorRef);
  public setupScreensService = inject(SetupScreensService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public featureFlagsService = inject(FeatureflagsService);

  zoneDetails: ZoneSetup = null;
  originalResponse: ZoneDetailsCompositeResponse = null;
  isLoaded = false;
  productSaveDisabled = true;
  saveEnabled = false;
  zoneForm: FormGroup;
  zoneSetupDetails: FormGroup;
  zonePosts: FormArray;
  zoneProcessCodes: FormArray;
  workFlowDetails: FormArray;
  zones: string[] = [
    this.translationService.translate('northAmericaText'),
    this.translationService.translate('europeText'),
    this.translationService.translate('southAmericaText')
  ];
  optCustCodes: string[] = [
    this.translationService.translate('cnpjText'),
    this.translationService.translate('shipToText') + ' ' + this.translationService.translate('codeText')
  ];
  ynOptions: string[] = [this.translationService.translate('yesText'), this.translationService.translate('noText')];
  rawMaterialOptions: string[] = [this.translationService.translate('metricText'), this.translationService.translate('englishText')];
  rawMaterialUOMOptions: string[] = [this.translationService.translate('unitsText'), this.translationService.translate('weightText')];
  dateDisplayOptions: string[] = [this.translationService.translate('MM/dd/yyyy'), this.translationService.translate('dd/MM/yyyy')];
  ynrOptions: string[] = ['R', 'Y', 'N'];

  workflowItemsRR: string[] = [];
  workflowItemsRO: string[] = [];
  workflowItemsRS: string[] = [];
  postNames = [
    this.translationService.translate('postText') + '1',
    this.translationService.translate('postText') + '2',
    this.translationService.translate('postText') + '3',
    this.translationService.translate('postText') + '4',
    this.translationService.translate('postText') + '5',
    this.translationService.translate('postText') + '6',
    this.translationService.translate('postText') + '7',
    this.translationService.translate('postText') + '8',
    this.translationService.translate('postText') + '9',
    this.translationService.translate('postText') + '10'
  ];

  constructor() {}

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled;
    this.initZoneSetupDetailsForm();
    this.zonePosts = this.createZonePostsFormArray();
    this.zoneProcessCodes = this.fb.array([]);
    this.workFlowDetails = this.fb.array([]);
    this.zoneForm = this.fb.group({
      zoneSetupDetails: this.zoneSetupDetails,
      zonePosts: this.zonePosts,
      zoneProcessCodes: this.zoneProcessCodes,
      workFlowDetails: this.workFlowDetails
    });
    this.loadZoneDetails();
  }

  private initZoneSetupDetailsForm(): void {
    this.zoneSetupDetails = this.fb.group({
      zoneId: ['', Validators.required],
      exportDirectory: ['', Validators.required],
      adjCalcMthd: ['', Validators.required],
      optCustCode: [''],
      reuseBarCodes: [false],
      printBarCodes: [false],
      workOrderPrefix: [''],
      checkCamSpecs: [false],
      rawMaterials: ['', Validators.required],
      rawMaterialsUom: ['', Validators.required],
      dateDisplay: ['', Validators.required],
      treadPicture: [false],
      maxNumLogin: [''],
      allowCasingsAtDifferentPlants: [false],
      differentPlantNewWO: [false],
      ntr: [''],
      buffedCircumference: [''],
      loadI: [''],
      speedI: [''],
      serialLotForTread: [''],
      mfgDot: [''],
      ciaPassFail: [''],
      lotNumberForCtc: [''],
      tread: [''],
      rubberThickness: [''],
      dueDate: [false],
      lotNumberForRepairs: [''],
      width: [''],
      cnpjShipToCode: [''],
      factoryCode: [''],
      lotNumberForCementGlue: [''],
      length: [''],
      ctc: [''],
      sequenceNumber: [''],
      lotNumberForRepairGum: [''],
      curingResult: [''],
      reworkR1Enable: [''],
      reworkR3Enable: [''],
      rerunBEnable: [''],
      casingDead: [''],
      reworkRFIDEnable: [''],
      archiveActive: [''],
      purgeActive: ['']
    });
  }

  private loadZoneDetails(): void {
    this.setupScreensService.getZoneDetails().subscribe({
      next: (response) => {
        this.originalResponse = response.data;
        const zone = response.data.zoneDetails;
        this.zoneDetails = zone;
        this.zoneSetupDetails.patchValue({
          zoneId: this.mapZoneId(zone.zoneId) ?? '',
          exportDirectory: zone.exportDirectory ?? '',
          adjCalcMthd: zone.adjCalMethod ?? '',
          optCustCode: this.mapOptCustCode(zone.optCustCode) ?? '',
          reuseBarCodes: this.mapYesNo(zone.reuseBarCodeNum) ?? '',
          printBarCodes: this.mapYesNo(zone.printBarcodeEnable) ?? '',
          workOrderPrefix: zone.workOrderPrefix ?? '',
          checkCamSpecs: this.mapYesNo(zone.checkCamSpecs) ?? '',
          rawMaterials: this.mapRawMaterials(zone.rawMaterialsMethod) ?? '',
          rawMaterialsUom: this.mapRawMaterialsUom(zone.rawMaterialsUom) ?? '',
          dateDisplay: zone.dateDisplay ?? '',
          treadPicture: this.mapYesNo(zone.treadPictureEnable) ?? '',
          maxNumLogin: zone.failureThreshold ?? '',
          allowCasingsAtDifferentPlants: this.mapAllowCasingsAtDifferentPlants(zone.allowDifferentPlant) ?? '',
          differentPlantNewWO: this.mapAllowCasingsAtDifferentPlants(zone.differentPlantNewWO) ?? '',
          ntr: zone.numTimesRtd ?? '',
          buffedCircumference: zone.buffCircumRequired ?? '',
          loadI: zone.loadIndex ?? '',
          speedI: zone.speedIndex ?? '',
          serialLotForTread: zone.serialNum ?? '',
          mfgDot: zone.mfgDotRequired ?? '',
          ciaPassFail: zone.ciaPassFailFlag ?? '',
          lotNumberForCtc: zone.lotNumberOnCtc ?? '',
          tread: zone.treadWidth ?? '',
          rubberThickness: zone.rubberThickness ?? '',
          dueDate: this.mapYesNo(zone.dueDay) ?? '',
          lotNumberForRepairs: zone.lotNumberOnRepair ?? '',
          width: zone.treadWidth ?? '',
          cnpjShipToCode: zone.optCustCodeReq ?? '',
          factoryCode: zone.factoryCode ?? '',
          lotNumberForCementGlue: zone.lotNumberCementGlue ?? '',
          length: zone.treadLength ?? '',
          ctc: zone.ctc ?? '',
          sequenceNumber: zone.sequenceNum ?? '',
          lotNumberForRepairGum: zone.lotNumberRepairGum ?? '',
          curingResult: zone.curingResults ?? '',
          reworkR1Enable: this.mapYesNo(zone.reworkR1Enable) ?? '',
          reworkR3Enable: this.mapYesNo(zone.reworkR3Enable) ?? '',
          rerunBEnable: this.mapYesNo(zone.rerunBEnable) ?? '',
          casingDead: this.mapYesNo(zone.casingDead) ?? '',
          reworkRFIDEnable: this.mapYesNo(zone.reworkRFIDEnable) ?? '',
          archiveActive: this.mapYesNo(zone.archiveActive) ?? '',
          purgeActive: this.mapYesNo(zone.purgeActive) ?? ''
        });

        const zonePosts = response.data.zonePosts;
        this.zonePosts.controls.forEach((control) => {
          const postName = control.get('postName')?.value as string;
          if (postName && zonePosts[postName] !== undefined) {
            const value = zonePosts[postName];
            control.patchValue({ postActive: value });
            control.get('postActive').enable({ emitEvent: false });
          }
        });

        const zoneProcessCodes = response.data.zoneProcessCodes;
        this.zoneProcessCodes = this.createZoneProcessCodesFormArray(zoneProcessCodes || []);
        this.isLoaded = true;

        const workFlowDetails = response.data.zoneWorkflowDetails;
        this.workFlowDetails = this.createWorkFlowDetailsFormArray(workFlowDetails || []);
        this.zoneForm.setControl('zoneProcessCodes', this.zoneProcessCodes);
        this.zoneForm.setControl('workFlowDetails', this.workFlowDetails);
        this.workFlowControlsCache.clear();

        this.workflowItemsRR = this.getWorkFlowControls('RR').map((ctrl) => ctrl.get('postName').value || '');
        this.workflowItemsRO = this.getWorkFlowControls('RO').map((ctrl) => ctrl.get('postName').value || '');
        this.workflowItemsRS = this.getWorkFlowControls('RS').map((ctrl) => ctrl.get('postName').value || '');
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('errorLoadingZoneDetailsText')
        } as Alert);
      }
    });
  }

  private createZonePostsFormArray(): FormArray {
    // Explicit order using EGRA_POSTS keys
    const postOrder: string[] = [
      'INITIAL_INSPECTION',
      'BUFFER',
      'CIA',
      'SKIVE',
      'REPAIR',
      'CEMENT_FILL',
      'BUILDER',
      'PRE_MOLD',
      'CUSTOM_MOLD',
      'CHAMBER',
      'ADJUSTMENT',
      'SERVICE',
      'FINAL_INSPECTION',
      'REJECT',
      'TREAD_PREPARATION'
    ];

    const groups = postOrder
      .filter((name) => EGRA_POSTS[name] && EGRA_POSTS[name].availableForPlantPosts)
      .map((name) =>
        this.fb.group({
          postName: [name],
          postActive: ['']
        })
      );

    return this.fb.array(groups);
  }

  private createZoneProcessCodesFormArray(codes: ZoneProcessCode[]): FormArray {
    return this.fb.array(
      codes.map((code) => {
        const group = this.fb.group({
          plantProcessCodePk: [code.plantProcessCodePk || ''],
          plantFk: [code.plantFk || ''],
          processCode: [code.processCode || ''],
          processCodeDescription: [code.processCodeDescription || ''],
          active: [code.active || '']
        });
        return group;
      })
    );
  }

  private createWorkFlowDetailsFormArray(details: WorkFlowDetails[]): FormArray {
    return this.fb.array(
      details.map((detail) =>
        this.fb.group({
          workFlowDetailsPk: [detail.workFlowDetailsPk || ''],
          plantFk: [detail.plantFk || ''],
          workType: [detail.workType || ''],
          workFlowOrder: [detail.workFlowOrder ?? null],
          postName: [detail.postName || '']
        })
      )
    );
  }

  private readonly workFlowControlsCache = new Map<string, FormGroup[]>();

  getWorkFlowControls(code: string): FormGroup[] {
    if (this.workFlowControlsCache.has(code)) {
      return this.workFlowControlsCache.get(code);
    }

    // Get existing controls for this work type and sort them
    const existingControls = this.workFlowDetails?.controls
      ? this.workFlowDetails.controls
          .filter((ctrl) => (ctrl as FormGroup).get('workType')?.value === code)
          .sort((a, b) => ((a as FormGroup).get('workFlowOrder')?.value ?? 0) - ((b as FormGroup).get('workFlowOrder')?.value ?? 0))
          .map((ctrl) => ctrl as FormGroup)
      : [];
    // Cache the result
    this.workFlowControlsCache.set(code, existingControls);

    return existingControls;
  }

  getWorkflowItems(workType: string): string[] {
    if (workType === 'RR') return this.workflowItemsRR;
    if (workType === 'RO') return this.workflowItemsRO;
    if (workType === 'RS') return this.workflowItemsRS;
    return [];
  }

  getWorkflowPostNames(workType: string): string[] {
    const usedPostNames =
      this.workFlowDetails?.controls?.filter((ctrl) => ctrl.get('workType').value === workType).map((ctrl) => ctrl.get('postName').value) ||
      [];
    return Object.keys(EGRA_POSTS)
      .filter((key) => EGRA_POSTS[key].availableForWorkflow && !usedPostNames.includes(key))
      .sort((a, b) => EGRA_POSTS[a].labelKey.localeCompare(EGRA_POSTS[b].labelKey));
  }

  drop(event: CdkDragDrop<string[]>, workType: string) {
    const items = this.getWorkflowItems(workType);
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    const workflowControls = this.getWorkFlowControls(workType);
    workflowControls.forEach((ctrl, idx) => {
      ctrl.get('postName').setValue(items[idx]);
      ctrl.get('workFlowOrder').setValue(idx + 1);
    });
    // Clear cache to ensure fresh+ data
    this.workFlowControlsCache.delete(workType);
    this.zoneForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onAddPost(postName: string, workType: string) {
    if (!postName) return;

    // Check if the post is marked as 'N' (inactive) in the posts section
    const postStatus = this.zonePosts.getRawValue().find((p) => p.postName === postName)?.postActive;
    if (postStatus === 'N') {
      this.alertService.showAlert({
        type: AlertType.strong,
        content:
          this.translationService.translate('invalidWorkflowPostsText') +
          ': ' +
          this.translationService.translate(this.getPostLabel(postName))
      } as Alert);
      return;
    }

    // Prevent duplicate posts in workflow for the same workType
    const items = this.getWorkflowItems(workType);
    if (items.includes(postName)) {
      return;
    }
    items.push(postName);
    const workflowControls = this.getWorkFlowControls(workType);
    const newControl = this.fb.group({
      workFlowDetailsPk: [''],
      plantFk: [this.zoneDetails?.id || ''],
      workType: [workType],
      workFlowOrder: [workflowControls.length + 1],
      postName: [postName]
    });
    this.workFlowDetails.push(newControl);
    // Clear cache to ensure fresh data
    this.workFlowControlsCache.delete(workType);
    this.zoneForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onDeleteWorkflowItem(index: number, workType: string) {
    const items = this.getWorkflowItems(workType);
    items.splice(index, 1);
    const workflowControls = this.getWorkFlowControls(workType);
    if (workflowControls?.[index]) {
      this.workFlowDetails.removeAt(this.workFlowDetails.controls.indexOf(workflowControls[index]));
    }
    // Clear cache before getting fresh controls
    this.workFlowControlsCache.delete(workType);
    this.getWorkFlowControls(workType).forEach((ctrl, idx) => {
      ctrl.get('workFlowOrder').setValue(idx + 1); // 1-based order
    });
    this.zoneForm.markAsDirty();
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  getPostLabel(postName: string): string {
    return EGRA_POSTS[postName]?.labelKey || postName;
  }

  // === Save / Cancel / Permissions ===

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    this.zoneSetupDetails.markAllAsTouched();

    if (!this.zoneSetupDetails.valid) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('missingValuesText')
      } as Alert);
      return;
    }

    if (!this.checkIfModified()) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
      return;
    }

    const payload = this.buildSavePayload();
    this.setupScreensService.updateZoneSetup(payload).subscribe({
      next: (response) => {
        this.originalResponse = response.data;
        this.zoneDetails = response.data.zoneDetails;
        this.zoneForm.markAsPristine();
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('zoneSetupUpdatedSuccessfullyText')
        } as Alert);
        this.loadZoneDetails();
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  cancel(): void {
    if (this.originalResponse) {
      this.loadZoneDetails();
      this.zoneForm.markAsPristine();
    }
  }

  checkIfModified(): boolean {
    return this.zoneForm?.dirty ?? false;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.saveEnabled && this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(this.checkIfModified.bind(this), this.discardModal, this.discardContent, this.translationService);
  }

  private buildSavePayload(): any {
    const formValues = this.zoneSetupDetails.value;
    const zonePosts = this.zonePosts.getRawValue();
    const zoneProcessCodes = this.zoneProcessCodes?.getRawValue() || [];
    const workFlowDetails = this.workFlowDetails?.getRawValue() || [];

    return {
      zoneId: this.reverseMapZoneId(formValues.zoneId),
      exportDirectory: formValues.exportDirectory,
      adjCalMethod: formValues.adjCalcMthd,
      optCustCode: this.reverseMapOptCustCode(formValues.optCustCode),
      reuseBarCodeNum: this.reverseMapYesNo(formValues.reuseBarCodes),
      printBarcodeEnable: this.reverseMapYesNo(formValues.printBarCodes),
      workOrderPrefix: formValues.workOrderPrefix,
      checkCamSpecs: this.reverseMapYesNo(formValues.checkCamSpecs),
      rawMaterialsMethod: this.reverseMapRawMaterials(formValues.rawMaterials),
      rawMaterialsUom: this.reverseMapRawMaterialsUom(formValues.rawMaterialsUom),
      dateDisplay: formValues.dateDisplay,
      treadPictureEnable: this.reverseMapYesNo(formValues.treadPicture),
      failureThreshold: formValues.maxNumLogin,
      allowDifferentPlant: this.reverseMapYesNo(formValues.allowCasingsAtDifferentPlants) ? 'Y' : 'N',
      differentPlantNewWO: this.reverseMapYesNo(formValues.differentPlantNewWO) ? 'Y' : 'N',
      numTimesRtd: formValues.ntr,
      buffCircumRequired: formValues.buffedCircumference,
      loadIndex: formValues.loadI,
      speedIndex: formValues.speedI,
      serialNum: formValues.serialLotForTread,
      mfgDotRequired: formValues.mfgDot,
      ciaPassFailFlag: formValues.ciaPassFail,
      lotNumberOnCtc: formValues.lotNumberForCtc,
      treadWidth: formValues.tread,
      rubberThickness: formValues.rubberThickness,
      dueDay: this.reverseMapYesNo(formValues.dueDate),
      lotNumberOnRepair: formValues.lotNumberForRepairs,
      optCustCodeReq: formValues.cnpjShipToCode,
      factoryCode: formValues.factoryCode,
      lotNumberCementGlue: formValues.lotNumberForCementGlue,
      treadLength: formValues.length,
      ctc: formValues.ctc,
      sequenceNum: formValues.sequenceNumber,
      lotNumberRepairGum: formValues.lotNumberForRepairGum,
      curingResults: formValues.curingResult,
      reworkR1Enable: this.reverseMapYesNo(formValues.reworkR1Enable),
      reworkR3Enable: this.reverseMapYesNo(formValues.reworkR3Enable),
      rerunBEnable: this.reverseMapYesNo(formValues.rerunBEnable),
      casingDead: this.reverseMapYesNo(formValues.casingDead),
      reworkRFIDEnable: this.reverseMapYesNo(formValues.reworkRFIDEnable),
      archiveActive: this.reverseMapYesNo(formValues.archiveActive),
      purgeActive: this.reverseMapYesNo(formValues.purgeActive),
      zonePosts: zonePosts.reduce((acc, post) => {
        acc[post.postName] = post.postActive;
        return acc;
      }, {} as { [key: string]: string }),
      zoneProcessCodes: zoneProcessCodes,
      zoneWorkflowDetails: workFlowDetails
    };
  }

  // === Reverse Mappers (display value → DB value) ===

  private reverseMapYesNo(value: string): boolean {
    return value === this.translationService.translate('yesText');
  }

  private reverseMapZoneId(value: string): string {
    if (value === this.translationService.translate('northAmericaText')) return 'NA';
    if (value === this.translationService.translate('europeText')) return 'EUR';
    if (value === this.translationService.translate('southAmericaText')) return 'SA';
    return '';
  }

  private reverseMapOptCustCode(value: string): boolean {
    return value === this.translationService.translate('cnpjText');
  }

  private reverseMapRawMaterials(value: string): string {
    if (value === this.translationService.translate('metricText')) return 'M';
    if (value === this.translationService.translate('englishText')) return 'E';
    return '';
  }

  private reverseMapRawMaterialsUom(value: string): string {
    if (value === this.translationService.translate('unitsText')) return 'U';
    if (value === this.translationService.translate('weightText')) return 'W';
    return '';
  }

  private mapYesNo(value: boolean): string {
    return value ? this.translationService.translate('yesText') : this.translationService.translate('noText');
  }

  private mapZoneId(zoneId: string): string {
    switch (zoneId) {
      case 'NA':
        return this.translationService.translate('northAmericaText');
      case 'EUR':
        return this.translationService.translate('europeText');
      case 'SA':
        return this.translationService.translate('southAmericaText');
      default:
        return '';
    }
  }

  private mapOptCustCode(optCustCodeReq: boolean): string {
    return optCustCodeReq
      ? this.translationService.translate('cnpjText')
      : this.translationService.translate('shipToText') + ' ' + this.translationService.translate('codeText');
  }

  private mapRawMaterials(rawMaterialsMethod: string): string {
    switch (rawMaterialsMethod) {
      case 'M':
        return this.translationService.translate('metricText');
      case 'E':
        return this.translationService.translate('englishText');
      default:
        return '';
    }
  }

  private mapRawMaterialsUom(rawMaterialsUom: string): string {
    switch (rawMaterialsUom) {
      case 'U':
        return this.translationService.translate('unitsText');
      case 'W':
        return this.translationService.translate('weightText');
      default:
        return '';
    }
  }

  private mapAllowCasingsAtDifferentPlants(allowDifferentPlant: string): string {
    return allowDifferentPlant === 'Y' ? this.translationService.translate('yesText') : this.translationService.translate('noText');
  }

  onZoneLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      // TODO: Implement upload or preview logic here
      console.log('Selected zone logo file:', file);
    }
  }

  getProcessCodeControl(code: string, controlName: string): FormControl | null {
    const idx = this.zoneProcessCodes?.controls?.findIndex((ctrl) => ctrl.get('processCode').value === code);
    if (idx !== -1 && idx != null) {
      return this.zoneProcessCodes.at(idx).get(controlName) as FormControl;
    }
    return null;
  }
}
